import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { FileInput, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { z } from "zod";
import {
  InventoryImportProviderSchema,
  InventoryImportTypeSchema,
  InventoryRuleGroupSchema,
  type CreateInventoryImport,
  type InventoryImport,
  type InventoryImportProvider,
  type InventoryImportType,
  isScrapingProviderId,
  normalizeScrapeDomain,
} from "@lumika/shared";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  FormField,
  Input,
  MobileList,
  MobileListRow,
  PageError,
  PageHeader,
  PageLoading,
  Section,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBleed,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
  useZodForm,
} from "@lumika/ui";
import { adminApi } from "../../api/admin";
import { ApiError } from "../../api/client";
import { InventoryImportRuleEditor } from "../../components/InventoryImportRuleEditor";
import { formatDateTime, humanize } from "../../lib/format";

/**
 * Per-dealership "Inventory Imports" admin tab. Each row maps a
 * provider feed (vAuto / Space Auto / Homenet) and an exact SFTP file
 * path to a rooftop, optionally narrowed by a structured rule. The
 * ingestion consumer looks these rows up on every S3 ObjectCreated
 * event and reconciles `inventory_vehicles` against the file.
 *
 * Only `type='sftp'` is wired today; the scraping and api types
 * appear disabled (Coming soon) — the column accepts them so the UI
 * does not have to change when those ingestion sources land.
 */
export function DealershipInventoryImportsPage() {
  const { dealershipId } = useParams();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<InventoryImport | "new" | null>(null);
  // After a manual "Sync now", the scrape/pull runs asynchronously on the
  // app-backend and stamps `lastPulledAt`/`lastPulledError` seconds later.
  // Poll briefly so the row's sync status reflects the real outcome without
  // the operator having to refresh the page.
  const [lastSyncTs, setLastSyncTs] = useState<number | null>(null);

  const importsQuery = useQuery({
    queryKey: ["admin", "inventory-imports", dealershipId],
    queryFn: () => adminApi.listInventoryImports(dealershipId!),
    enabled: Boolean(dealershipId),
    refetchInterval: () =>
      lastSyncTs !== null && Date.now() - lastSyncTs < 20_000 ? 2_500 : false,
  });
  const rooftopsQuery = useQuery({
    queryKey: ["admin", "rooftops", dealershipId],
    queryFn: () => adminApi.listRooftops(dealershipId!),
    enabled: Boolean(dealershipId),
  });

  const rooftopName = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rooftopsQuery.data ?? []) map.set(r.id, r.name);
    return map;
  }, [rooftopsQuery.data]);

  const remove = useMutation({
    mutationFn: (id: string) =>
      adminApi.removeInventoryImport(dealershipId!, id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["admin", "inventory-imports", dealershipId] });
      toast.success("Inventory import deleted");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const sync = useMutation({
    mutationFn: (id: string) => adminApi.syncInventoryImport(dealershipId!, id),
    onSuccess: async () => {
      setLastSyncTs(Date.now());
      await qc.invalidateQueries({ queryKey: ["admin", "inventory-imports", dealershipId] });
      toast.success("Sync triggered — watch the Last sync column for the result");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const items = importsQuery.data ?? [];

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        eyebrow="Inventory"
        title="Inventory imports"
        description="Each row maps an SFTP file path to a provider and a rooftop. The ingestion consumer reconciles inventory_vehicles on every new upload."
        actions={
          <Button onClick={() => setEditing("new")} disabled={(rooftopsQuery.data ?? []).length === 0}>
            <Plus className="h-4 w-4" />
            Add import
          </Button>
        }
      />

      <Section title={`${items.length} imports`}>
        {importsQuery.isLoading ? (
          <PageLoading message="Loading inventory imports…" />
        ) : importsQuery.isError ? (
          <PageError title="Couldn't load inventory imports" description="Try refreshing the page." />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<FileInput className="h-5 w-5" />}
            title="No inventory imports yet"
            description="Add the first one — pick a provider, a rooftop, and the SFTP file path. Uploads to that path will be ingested automatically."
          />
        ) : (
          <>
            <TableBleed className="-my-6 hidden overflow-hidden rounded-b-lg md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Label</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Provider</TableHead>
                    <TableHead>Path</TableHead>
                    <TableHead>Rooftop</TableHead>
                    <TableHead>Rule</TableHead>
                    <TableHead>Last sync</TableHead>
                    <TableHead>Updated</TableHead>
                    <TableHead className="text-right">Edit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((imp) => (
                    <TableRow key={imp.id}>
                      <TableCell className="font-medium">{imp.label}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{humanize(imp.type)}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{humanize(imp.provider)}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {imp.path ?? "—"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {rooftopName.get(imp.rooftopId) ?? "—"}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {imp.rule ? `${imp.rule.combinator.toUpperCase()} · ${imp.rule.rules.length} rule(s)` : "All rows"}
                      </TableCell>
                      <TableCell>
                        <SyncStatus imp={imp} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {formatDateTime(imp.updatedAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Sync import now"
                            title="Sync now"
                            disabled={sync.isPending}
                            onClick={() => sync.mutate(imp.id)}
                          >
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Edit import"
                            onClick={() => setEditing(imp)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Delete import"
                            disabled={remove.isPending}
                            onClick={() => {
                              if (window.confirm(`Delete inventory import "${imp.label}"?`)) {
                                remove.mutate(imp.id);
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableBleed>
            <MobileList>
              {items.map((imp) => (
                <MobileListRow
                  key={imp.id}
                  primary={imp.label}
                  secondary={imp.path ?? "—"}
                  tertiary={rooftopName.get(imp.rooftopId) ?? "—"}
                  meta={
                    <>
                      <Badge variant="secondary">{humanize(imp.type)}</Badge>
                      <Badge variant="secondary">{humanize(imp.provider)}</Badge>
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        {imp.rule
                          ? `${imp.rule.combinator.toUpperCase()} · ${imp.rule.rules.length} rule(s)`
                          : "All rows"}
                      </span>
                      <SyncStatus imp={imp} />
                    </>
                  }
                  trailing={
                    <div className="flex gap-1">
                      {imp.type !== "sftp" ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Sync import now"
                          title="Sync now"
                          disabled={sync.isPending}
                          onClick={() => sync.mutate(imp.id)}
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      ) : null}
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Edit import"
                        onClick={() => setEditing(imp)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Delete import"
                        disabled={remove.isPending}
                        onClick={() => {
                          if (window.confirm(`Delete inventory import "${imp.label}"?`)) {
                            remove.mutate(imp.id);
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  }
                />
              ))}
            </MobileList>
          </>
        )}
      </Section>


      {editing !== null && (
        <ImportFormDialog
          dealershipId={dealershipId!}
          rooftops={(rooftopsQuery.data ?? []).map((r) => ({ id: r.id, name: r.name }))}
          editing={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            await qc.invalidateQueries({ queryKey: ["admin", "inventory-imports", dealershipId] });
          }}
        />
      )}
    </div>
  );
}

export type SyncStatusKind = "on-upload" | "failed" | "synced" | "never";

/**
 * Classify an import's last-pull outcome for display. A present
 * `lastPulledError` ALWAYS wins over a stale prior success — a failed
 * scrape must never read as healthy. SFTP imports have no pull cadence
 * (they ingest on file upload), so they're "on-upload" regardless of
 * the pull columns. Exported so the precedence is unit-tested.
 */
export function inventoryImportSyncStatus(
  imp: Pick<InventoryImport, "type" | "lastPulledAt" | "lastPulledError">,
): SyncStatusKind {
  if (imp.type === "sftp") return "on-upload";
  if (imp.lastPulledError) return "failed";
  if (imp.lastPulledAt) return "synced";
  return "never";
}

/**
 * Per-import sync status, derived from the read DTO's `lastPulledAt` /
 * `lastPulledError`. This is what makes the "Sync now" button observable
 * for scraping/api imports: a failed pull shows a red badge with the
 * error on hover, a healthy one shows when it last landed, and one that
 * has never run shows "Never". SFTP imports have no pull cadence — they
 * ingest on file upload — so they read "On upload".
 */
function SyncStatus({ imp }: { imp: InventoryImport }) {
  switch (inventoryImportSyncStatus(imp)) {
    case "on-upload":
      return <span className="text-xs text-muted-foreground">On upload</span>;
    case "failed":
      return (
        <span className="inline-flex flex-col gap-0.5">
          <Badge variant="destructive" title={imp.lastPulledError ?? undefined}>
            Failed
          </Badge>
          {imp.lastPulledAt ? (
            <span className="text-[10px] text-muted-foreground">
              {formatDateTime(imp.lastPulledAt)}
            </span>
          ) : null}
        </span>
      );
    case "synced":
      return (
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {formatDateTime(imp.lastPulledAt!)}
        </span>
      );
    case "never":
      return <span className="text-xs text-muted-foreground">Never</span>;
  }
}

// Mirrors `CreateInventoryImportSchema` but holds text inputs as "" while
// typing (submit-time maps ""→null for the server). `path`/`token` are
// validated by the same max(512) bound; the cross-field requirements
// (sftp→path, scraping→URL) live in the refinement below so a bad value
// shows inline instead of bouncing off a server 400. The api→token rule
// depends on edit context (a blank token keeps the stored one), so it is
// enforced at submit, mirroring the original dialog.
const ImportFormSchema = z
  .object({
    label: z.string().trim().min(1, "Label is required.").max(120),
    type: InventoryImportTypeSchema,
    provider: InventoryImportProviderSchema,
    rooftopId: z.string().uuid(),
    path: z.string().trim().max(512),
    token: z.string().trim().max(512),
    ccid: z.string().trim().max(32),
    rule: InventoryRuleGroupSchema.nullable(),
  })
  .superRefine((v, ctx) => {
    if (v.type === "sftp" && v.path.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Path is required for SFTP imports.",
        path: ["path"],
      });
    }
    if (
      v.type === "scraping" &&
      v.path.length > 0 &&
      !/^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(normalizeScrapeDomain(v.path))
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Use a domain, e.g. example.com.",
        path: ["path"],
      });
    }
    if (v.ccid.length > 0 && !/^\d{4,9}$/.test(v.ccid)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Cars Commerce listing id must be numeric.",
        path: ["ccid"],
      });
    }
  });

type ImportFormValues = z.infer<typeof ImportFormSchema>;

// Map a nestjs-zod 400 (or 409) back onto the dialog's fields so it
// surfaces inline. Server issue paths are flat here (e.g. ["path"]).
const SERVER_PATH_TO_FIELD: Record<string, keyof ImportFormValues> = {
  label: "label",
  type: "type",
  provider: "provider",
  rooftopId: "rooftopId",
  path: "path",
  token: "token",
  ccid: "ccid",
  rule: "rule",
};

function mapServerErrorToFields(
  error: unknown,
): Partial<Record<keyof ImportFormValues, string>> | null {
  if (!(error instanceof ApiError)) return null;
  const body = error.details;
  const issues =
    body && typeof body === "object" && Array.isArray((body as { errors?: unknown }).errors)
      ? (body as { errors: Array<{ path?: unknown; message?: unknown }> }).errors
      : null;
  if (error.status === 400 && issues) {
    const out: Partial<Record<keyof ImportFormValues, string>> = {};
    for (const issue of issues) {
      const path = Array.isArray(issue.path) ? issue.path : [];
      const key = path.filter((p) => typeof p === "string").join(".");
      const field = SERVER_PATH_TO_FIELD[key];
      if (field && !(field in out) && typeof issue.message === "string") {
        out[field] = issue.message;
      }
    }
    return Object.keys(out).length > 0 ? out : null;
  }
  return null;
}

function ImportFormDialog({
  dealershipId,
  rooftops,
  editing,
  onClose,
  onSaved,
}: {
  dealershipId: string;
  rooftops: { id: string; name: string }[];
  editing: InventoryImport | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  // Initial values double as the dirty-check baseline below.
  const initialType = editing?.type ?? "sftp";
  // Legacy scraping rows carry the retired 'website' provider, which the
  // platform select no longer offers — surface a real platform so saving
  // upgrades the row instead of silently re-saving a provider every
  // scrape run rejects.
  const initialProvider =
    initialType === "scraping" && !isScrapingProviderId(editing?.provider ?? "")
      ? "dealeron"
      : (editing?.provider ?? "vauto");
  const initialValues: ImportFormValues = {
    label: editing?.label ?? "",
    type: initialType,
    provider: initialProvider,
    rooftopId: editing?.rooftopId ?? rooftops[0]?.id ?? "",
    path: editing?.path ?? "",
    token: "",
    ccid: editing?.ccid ?? "",
    rule: editing?.rule ?? null,
  };
  const form = useZodForm(ImportFormSchema, initialValues);
  const { values, errors, setField } = form;
  // Once the operator has typed anything, block the implicit close paths
  // (outside click, Escape) so a stray click can't wipe the form.
  const isDirty = JSON.stringify(values) !== JSON.stringify(initialValues);

  const create = useMutation({
    mutationFn: (body: CreateInventoryImport) =>
      adminApi.createInventoryImport(dealershipId, body),
    onSuccess: async () => {
      await onSaved();
      toast.success("Inventory import created");
      onClose();
    },
    onError: (e: Error) => {
      const fieldErrors = mapServerErrorToFields(e);
      if (fieldErrors) form.setServerErrors(fieldErrors);
      toast.error(e.message);
    },
  });
  const update = useMutation({
    mutationFn: (body: ImportFormValues) =>
      adminApi.updateInventoryImport(dealershipId, editing!.id, {
        label: body.label,
        type: body.type,
        provider: body.provider,
        rooftopId: body.rooftopId,
        path: body.path || null,
        ccid: body.ccid || null,
        rule: body.rule,
        // Only send token when the operator typed a new one. An
        // empty input on an edit means "keep the stored value".
        ...(body.token.length > 0 ? { token: body.token } : {}),
      }),
    onSuccess: async () => {
      await onSaved();
      toast.success("Inventory import saved");
      onClose();
    },
    onError: (e: Error) => {
      const fieldErrors = mapServerErrorToFields(e);
      if (fieldErrors) form.setServerErrors(fieldErrors);
      toast.error(e.message);
    },
  });
  const busy = create.isPending || update.isPending;

  function submit() {
    const result = form.validate();
    if (!result.ok) return;
    // The api→token requirement is edit-context-dependent (a blank token
    // on edit keeps the stored one), so it lives here rather than in the
    // schema.
    if (result.data.type === "api" && !editing && result.data.token.length === 0) {
      form.setServerErrors({ token: "Token is required for API imports." });
      return;
    }
    if (editing) {
      update.mutate(result.data);
    } else {
      create.mutate({
        rooftopId: result.data.rooftopId,
        type: result.data.type,
        provider: result.data.provider,
        rule: result.data.rule,
        path: result.data.path || null,
        label: result.data.label,
        token: result.data.token || null,
        ccid: result.data.ccid || null,
      });
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" preventDismiss={isDirty}>
        <DialogHeader>
          <DialogTitle>{editing ? "Edit inventory import" : "Add inventory import"}</DialogTitle>
          <DialogDescription>
            Maps a provider feed file to a rooftop. The ingestion consumer reads this on every S3 ObjectCreated event.
          </DialogDescription>
        </DialogHeader>

        <div className="grid max-h-[60vh] gap-4 overflow-y-auto -mx-1 px-1">
          <FormField label="Label" required error={errors.label}>
            <Input
              value={values.label}
              onChange={(e) => setField("label", e.target.value)}
              placeholder="e.g. vAuto BMW (Downtown)"
              autoFocus
            />
          </FormField>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Type" error={errors.type}>
              <Select
                value={values.type}
                onValueChange={(v) => {
                  const next = v as InventoryImportType;
                  setField("type", next);
                  // Each type owns a provider family — swap to a sane
                  // default when crossing the boundary.
                  if (next === "scraping" && !isScrapingProviderId(values.provider)) {
                    setField("provider", "dealeron");
                  } else if (next !== "scraping" && isScrapingProviderId(values.provider)) {
                    setField("provider", "vauto");
                  } else if (next === "api" && values.provider === "lumika-json") {
                    // lumika-json is an uploaded-file provider — sftp only.
                    setField("provider", "vauto");
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sftp">SFTP</SelectItem>
                  <SelectItem value="scraping">Website Scraping</SelectItem>
                  <SelectItem value="api">API</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField
              label={values.type === "scraping" ? "Platform" : "Provider"}
              error={errors.provider}
            >
              <Select
                value={values.provider}
                onValueChange={(v) => setField("provider", v as InventoryImportProvider)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {values.type === "scraping" ? (
                    <>
                      <SelectItem value="dealeron">DealerOn</SelectItem>
                      <SelectItem value="dealercom">Dealer.com</SelectItem>
                      <SelectItem value="dealercloud">dealr.cloud</SelectItem>
                      <SelectItem value="teamvelocity">Team Velocity</SelectItem>
                      <SelectItem value="overfuel">Overfuel</SelectItem>
                      <SelectItem value="dealerinspire">DealerInspire</SelectItem>
                    </>
                  ) : (
                    <>
                      <SelectItem value="vauto">vAuto</SelectItem>
                      <SelectItem value="spaceauto">Space Auto</SelectItem>
                      <SelectItem value="homenet">Homenet</SelectItem>
                      {values.type === "sftp" ? (
                        <SelectItem value="lumika-json">Lumika JSON</SelectItem>
                      ) : null}
                    </>
                  )}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Rooftop" required error={errors.rooftopId}>
              <Select value={values.rooftopId} onValueChange={(v) => setField("rooftopId", v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {rooftops.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            {values.type === "sftp" ? (
              <FormField
                label="Path (S3 key)"
                required
                error={errors.path}
                description="Exact S3 key uploaded to the SFTP bucket — e.g. vauto/bmw.csv. Leading slashes are stripped automatically."
              >
                <Input
                  value={values.path}
                  onChange={(e) => setField("path", e.target.value)}
                  placeholder="vauto/bmw.csv"
                  className="font-mono"
                />
              </FormField>
            ) : null}
            {values.type === "scraping" ? (
              <FormField
                label="Domain"
                error={errors.path}
                description="The dealership site to scrape, e.g. example-motors.com — scheme and www are stripped automatically. Optional: defaults to the rooftop's website. The first scrape starts immediately on save; thereafter nightly at 7:00 UTC."
              >
                <Input
                  value={values.path}
                  onChange={(e) => setField("path", e.target.value)}
                  onBlur={() => setField("path", normalizeScrapeDomain(values.path))}
                  placeholder="example-motors.com"
                  className="font-mono"
                />
              </FormField>
            ) : null}
            {values.type === "scraping" && values.provider === "dealerinspire" ? (
              <FormField
                label="Listing ID (ccid)"
                error={errors.ccid}
                description="Optional. Only needed when the site's Cloudflare blocks auto-discovery — paste the Cars Commerce listing id (the number in the inventory page's window.SEARCH_SERVICE / listings/<id>) to scrape it directly."
              >
                <Input
                  value={values.ccid}
                  onChange={(e) => setField("ccid", e.target.value)}
                  placeholder="e.g. 6070090"
                  className="font-mono"
                />
              </FormField>
            ) : null}
            {values.type === "api" ? (
              <FormField
                label="API token"
                required={!editing}
                error={errors.token}
                description={
                  editing
                    ? "Leave blank to keep the stored token. Type a new value to rotate."
                    : "Provider API key — used by the hourly pull cron. Encrypted at rest."
                }
              >
                <Input
                  type="password"
                  value={values.token}
                  onChange={(e) => setField("token", e.target.value)}
                  placeholder={
                    editing?.tokenMasked
                      ? `Current: ${editing.tokenMasked}`
                      : "paste API key"
                  }
                  className="font-mono"
                />
              </FormField>
            ) : null}
          </div>

          <FormField
            label="Filter rule"
            description={
              "Decide which parsed rows belong to this rooftop. Rules are grouped: a group with ALL combines its rules with AND (every condition must hold); a group with ANY combines them with OR (at least one). Groups can nest. Empty rule = every row passes."
            }
          >
            <InventoryImportRuleEditor
              value={values.rule}
              onChange={(next) => setField("rule", next)}
              disabled={busy}
            />
          </FormField>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy}>
            {busy ? "Saving…" : editing ? "Save changes" : "Create import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
